import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Sparkles, Clock, Shield, Star, CheckCircle2, Droplets, Car, Zap, Loader2, Phone, Mail, MapPin, Menu, X, MessageSquare, Image as ImageIcon, Calendar, HelpCircle, Briefcase, Search, Award, Heart, FileText, User as UserIcon, LogOut, Package } from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ThemeToggle";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { PopupDisplay } from "@/components/PopupDisplay";
import { useAuth } from "@/contexts/AuthContext";
import { useActiveServices, useBrandSettings, useFAQs, useFeatures, useHomePageSections, useContactSettings, useAllTestimonials, useActiveSubscriptionPackages } from "@/hooks/useFirestore";
import { formatCurrency } from "@/lib/utils";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export default function Landing() {
  const { user, signIn, signOut } = useAuth();
  const [, setLocation] = useLocation();
  const { data: dbServices, isLoading: servicesLoading } = useActiveServices();
  const { data: firestoreBrandSettings } = useBrandSettings();
  const { data: faqs = [] } = useFAQs();
  const { data: features = [] } = useFeatures();
  const { data: sections = [] } = useHomePageSections();
  const { data: contactSettings } = useContactSettings();
  const { data: testimonials = [] } = useAllTestimonials();
  const { data: subscriptionPackages = [] } = useActiveSubscriptionPackages();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSignupBanner, setShowSignupBanner] = useState(() => {
    // Check if user has dismissed the banner before
    const dismissed = localStorage.getItem('signup-banner-dismissed');
    return !user && !dismissed;
  });

  // Initialize brandSettings from cache (instant, no FOUC)
  const [brandSettings, setBrandSettings] = useState(() => {
    if (typeof window !== 'undefined' && (window as any).__cachedBrandSettings) {
      return (window as any).__cachedBrandSettings;
    }
    return null;
  });

  // Update brandSettings when Firestore data arrives
  useEffect(() => {
    if (firestoreBrandSettings) {
      setBrandSettings(firestoreBrandSettings);
      
      // Update caches for next load
      const COLORS_KEY = 'temizfilo_brand_colors';
      const SETTINGS_KEY = 'temizfilo_brand_settings';
      
      if (firestoreBrandSettings.colors) {
        localStorage.setItem(COLORS_KEY, JSON.stringify(firestoreBrandSettings.colors));
        
        // Also update CSS variables
        const root = document.documentElement;
        Object.entries(firestoreBrandSettings.colors).forEach(([key, value]) => {
          root.style.setProperty(`--${key}`, value as string);
        });
      }
      
      // Cache full brand settings for hero, etc.
      localStorage.setItem(SETTINGS_KEY, JSON.stringify(firestoreBrandSettings));
    }
  }, [firestoreBrandSettings]);

  // Redirect employees, cashiers, and admins to their respective dashboards
  useEffect(() => {
    if (user && user.role !== 'customer') {
      setLocation(`/${user.role}`);
    }
  }, [user, setLocation]);
  
  const handleDismissBanner = () => {
    localStorage.setItem('signup-banner-dismissed', 'true');
    setShowSignupBanner(false);
  };

  const services = [
    {
      id: "fallback-1",
      title: "Dış Yıkama",
      description: "Kapsamlı dış temizlik ve cilalama",
      duration: 30,
      icon: Car,
      pricing: { sedan: 150, suv: 200, large: 250 },
    },
    {
      id: "fallback-2",
      title: "İç Temizlik",
      description: "Detaylı iç mekan temizliği",
      duration: 45,
      icon: Sparkles,
      pricing: { sedan: 200, suv: 250, large: 300 },
    },
    {
      id: "fallback-3",
      title: "Komple Bakım",
      description: "Dış + iç tam bakım paketi",
      duration: 75,
      icon: Star,
      pricing: { sedan: 350, suv: 450, large: 550 },
    },
    {
      id: "fallback-4",
      title: "Detaylı Yıkama",
      description: "Motor temizliği ve detaylı bakım",
      duration: 90,
      icon: Zap,
      pricing: { sedan: 400, suv: 500, large: 600 },
    },
  ];

  const pricing = [
    { type: "Binek Araç", base: 150, premium: 250, full: 400 },
    { type: "SUV", base: 200, premium: 300, full: 500 },
    { type: "Büyük Araç", base: 250, premium: 350, full: 600 },
  ];

  // Use stats from brandSettings, fallback to defaults
  const stats = brandSettings?.stats?.length ? brandSettings.stats : [
    { value: "10,000+", label: "Mutlu Müşteri", icon: "users" },
    { value: "4.9/5", label: "Ortalama Puan", icon: "star" },
    { value: "15+", label: "Yıllık Tecrübe", icon: "award" },
    { value: "24/7", label: "Müşteri Desteği", icon: "clock" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <PopupDisplay pageId="landing" />
      
      {/* Signup Banner for non-logged users */}
      {showSignupBanner && !user && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-primary text-primary-foreground">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5" />
                <p className="text-sm font-medium">
                  Hesap oluşturarak araçlarınızı kaydedin ve hızlıca randevu alın!
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Link href="/register">
                  <Button
                    variant="secondary"
                    size="sm"
                    data-testid="banner-signup"
                    onClick={() => setShowSignupBanner(false)}
                  >
                    Kayıt Ol
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleDismissBanner}
                  data-testid="banner-dismiss"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Navigation */}
      <nav className={`fixed left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-lg ${showSignupBanner && !user ? 'top-[52px]' : 'top-0'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link href="/">
              <div className="flex items-center gap-2 cursor-pointer hover-elevate active-elevate-2 px-3 py-2 rounded-lg transition-all" data-testid="link-home">
                <Droplets className="w-8 h-8 text-primary" />
                <span className="text-xl font-bold text-primary">TemizFilo</span>
              </div>
            </Link>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center gap-1">
              <Link href="/gallery">
                <Button variant="ghost" size="sm" data-testid="button-gallery">
                  <ImageIcon className="w-4 h-4 mr-2" />
                  Galeri
                </Button>
              </Link>
              <Link href="/services">
                <Button variant="ghost" size="sm" data-testid="button-services">
                  <Droplets className="w-4 h-4 mr-2" />
                  Hizmetler
                </Button>
              </Link>
              <Link href="/contact">
                <Button variant="ghost" size="sm" data-testid="button-contact">
                  <Phone className="w-4 h-4 mr-2" />
                  İletişim
                </Button>
              </Link>
              <Link href="/feedback">
                <Button variant="ghost" size="sm" data-testid="button-feedback">
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Öneri
                </Button>
              </Link>
              <Link href="/blog">
                <Button variant="ghost" size="sm" data-testid="button-blog">
                  <FileText className="w-4 h-4 mr-2" />
                  Blog
                </Button>
              </Link>
              <Link href="/subscriptions">
                <Button variant="ghost" size="sm" data-testid="button-subscriptions">
                  <Package className="w-4 h-4 mr-2" />
                  Abonelikler
                </Button>
              </Link>
              <Link href="/corporate-quote">
                <Button variant="ghost" size="sm" data-testid="button-corporate">
                  <Briefcase className="w-4 h-4 mr-2" />
                  Kurumsal Teklif
                </Button>
              </Link>
              
              {user ? (
                <>
                  {user.role === "customer" && (
                    <Link href="/book">
                      <Button data-testid="button-book-now">
                        Randevu Al
                      </Button>
                    </Link>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="rounded-full" data-testid="button-user-menu">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.photoURL || undefined} alt={user.displayName || undefined} />
                          <AvatarFallback>{user.displayName?.charAt(0) || "U"}</AvatarFallback>
                        </Avatar>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>
                        <div className="flex flex-col">
                          <span className="font-medium">{user.displayName}</span>
                          <span className="text-xs text-muted-foreground">{user.email}</span>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <Link href={user.role === "customer" ? "/customer" : `/${user.role}`}>
                        <DropdownMenuItem data-testid="menu-dashboard">
                          <UserIcon className="w-4 h-4 mr-2" />
                          {user.role === "customer" ? "Müşteri Paneli" : 
                           user.role === "employee" ? "Çalışan Paneli" :
                           user.role === "cashier" ? "Kasiyer Paneli" :
                           "Admin Paneli"}
                        </DropdownMenuItem>
                      </Link>
                      {user.role === "customer" && (
                        <Link href="/customer/profile">
                          <DropdownMenuItem data-testid="menu-profile">
                            <UserIcon className="w-4 h-4 mr-2" />
                            Profilim
                          </DropdownMenuItem>
                        </Link>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        onClick={() => signOut()} 
                        className="text-destructive focus:text-destructive"
                        data-testid="menu-logout"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Çıkış Yap
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <Link href="/login">
                    <Button variant="ghost" data-testid="button-signin">
                      Giriş Yap
                    </Button>
                  </Link>
                  <Link href="/book">
                    <Button data-testid="button-register">
                      Randevu Al
                    </Button>
                  </Link>
                </>
              )}
              <ThemeToggle />
            </div>
            
            {/* Mobile Menu */}
            <div className="flex lg:hidden items-center gap-2">
              <ThemeToggle />
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="button-mobile-menu">
                    <Menu className="w-6 h-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[300px] sm:w-[400px] flex flex-col">
                  <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                      <Droplets className="w-6 h-6 text-primary" />
                      TemizFilo
                    </SheetTitle>
                  </SheetHeader>
                  <div className="flex flex-col gap-4 mt-8 overflow-y-auto flex-1 pb-4">
                    {user ? (
                      <>
                        <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                          <Avatar className="h-10 w-10">
                            <AvatarImage src={user.photoURL || undefined} alt={user.displayName || undefined} />
                            <AvatarFallback>{user.displayName?.charAt(0) || "U"}</AvatarFallback>
                          </Avatar>
                          <div className="flex flex-col flex-1 min-w-0">
                            <span className="font-medium truncate">{user.displayName}</span>
                            <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                          </div>
                        </div>
                        <Link href={user.role === "customer" ? "/customer" : `/${user.role}`} onClick={() => setMobileMenuOpen(false)}>
                          <Button className="w-full justify-start" data-testid="mobile-link-dashboard">
                            <UserIcon className="w-4 h-4 mr-2" />
                            {user.role === "customer" ? "Müşteri Paneli" : 
                             user.role === "employee" ? "Çalışan Paneli" :
                             user.role === "cashier" ? "Kasiyer Paneli" :
                             "Admin Paneli"}
                          </Button>
                        </Link>
                        {user.role === "customer" && (
                          <>
                            <Link href="/customer/profile" onClick={() => setMobileMenuOpen(false)}>
                              <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-profile">
                                <UserIcon className="w-4 h-4 mr-2" />
                                Profilim
                              </Button>
                            </Link>
                            <Link href="/book" onClick={() => setMobileMenuOpen(false)}>
                              <Button variant="outline" className="w-full justify-start" data-testid="mobile-link-book">
                                <Calendar className="w-4 h-4 mr-2" />
                                Randevu Al
                              </Button>
                            </Link>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <Link href="/book" onClick={() => setMobileMenuOpen(false)}>
                          <Button className="w-full justify-start" data-testid="mobile-link-book">
                            <Calendar className="w-4 h-4 mr-2" />
                            Randevu Al
                          </Button>
                        </Link>
                        <Link href="/track" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="outline" className="w-full justify-start" data-testid="mobile-link-track">
                            <Search className="w-4 h-4 mr-2" />
                            Randevularım
                          </Button>
                        </Link>
                        <Link href="/login" onClick={() => setMobileMenuOpen(false)}>
                          <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-login">
                            Giriş Yap
                          </Button>
                        </Link>
                      </>
                    )}
                    <Link href="/services" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-services">
                        <Droplets className="w-4 h-4 mr-2" />
                        Hizmetler
                      </Button>
                    </Link>
                    <Link href="/gallery" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-gallery">
                        <ImageIcon className="w-4 h-4 mr-2" />
                        Galeri
                      </Button>
                    </Link>
                    <Link href="/contact" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-contact">
                        <Phone className="w-4 h-4 mr-2" />
                        İletişim
                      </Button>
                    </Link>
                    <Link href="/feedback" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-feedback">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Şikayet ve Öneri
                      </Button>
                    </Link>
                    <Link href="/blog" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-blog">
                        <FileText className="w-4 h-4 mr-2" />
                        Blog
                      </Button>
                    </Link>
                    <Link href="/subscriptions" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-subscriptions">
                        <Package className="w-4 h-4 mr-2" />
                        Abonelikler
                      </Button>
                    </Link>
                    <Link href="/faq" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-faq">
                        <HelpCircle className="w-4 h-4 mr-2" />
                        Sıkça Sorulan Sorular
                      </Button>
                    </Link>
                    <Link href="/corporate-quote" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" className="w-full justify-start" data-testid="mobile-link-corporate">
                        <Briefcase className="w-4 h-4 mr-2" />
                        Kurumsal Teklif Al
                      </Button>
                    </Link>
                    
                    {user && (
                      <>
                        <div className="border-t my-2" />
                        <Button 
                          variant="ghost" 
                          className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10" 
                          onClick={() => {
                            setMobileMenuOpen(false);
                            signOut();
                          }}
                          data-testid="mobile-button-logout"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Çıkış Yap
                        </Button>
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden mt-16">
        {/* Background gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-accent/10" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <Badge className="mb-6 px-4 py-2 text-sm" data-testid="badge-hero">
            {brandSettings?.hero?.badge || "Premium Araç Yıkama Hizmeti"}
          </Badge>
          
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 text-primary">
            {brandSettings?.hero?.title || "Aracınız İçin Mükemmel Bakım"}
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            {brandSettings?.hero?.subtitle || "Profesyonel ekibimiz ve modern ekipmanlarımızla aracınıza hak ettiği bakımı sunuyoruz. Online randevu sistemi ile kolayca rezervasyon yapın."}
          </p>
          
          <div className="flex flex-col gap-4 justify-center items-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link href={brandSettings?.hero?.cta1Link || "/book"}>
                <Button size="lg" className="px-8 py-6 text-lg rounded-xl font-semibold bg-primary hover:bg-primary/90" data-testid="button-hero-book">
                  {brandSettings?.hero?.cta1Text || "Hemen Randevu Al"}
                </Button>
              </Link>
              <Link href={brandSettings?.hero?.cta2Link || "/track"}>
                <Button size="lg" className="px-8 py-6 text-lg rounded-xl bg-primary/90 hover:bg-primary/80 text-primary-foreground" data-testid="button-hero-track">
                  <Search className="w-5 h-5 mr-2" />
                  {brandSettings?.hero?.cta2Text || "Randevu Takip"}
                </Button>
              </Link>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link href={brandSettings?.hero?.cta3Link || "/services"}>
                <Button size="lg" className="px-8 py-6 text-lg rounded-xl bg-primary/80 hover:bg-primary/70 text-primary-foreground" data-testid="button-hero-cta3">
                  {brandSettings?.hero?.cta3Text || "Hizmetlerimiz"}
                </Button>
              </Link>
              <Link href={brandSettings?.hero?.cta4Link || (user ? "/customer" : "/login")}>
                <Button size="lg" className="px-8 py-6 text-lg rounded-xl bg-primary/70 hover:bg-primary/60 text-primary-foreground" data-testid="button-hero-cta4">
                  {brandSettings?.hero?.cta4Text || "Müşteri Paneli"}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat: { value: string; label: string; icon: string }, index: number) => {
              const iconMap: { [key: string]: any } = {
                'users': UserIcon,
                'star': Star,
                'award': Award,
                'clock': Clock,
              };
              const IconComponent = iconMap[stat.icon] || UserIcon;
              
              return (
                <div key={index} className="text-center" data-testid={`stat-card-${index}`}>
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-primary mb-2">
                    {stat.value}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground">
                    {stat.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-primary">Hizmetlerimiz</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Aracınızın ihtiyacına göre özel olarak tasarlanmış hizmet paketlerimiz
            </p>
          </div>

          {dbServices && dbServices.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {dbServices.filter(s => s.isFeatured).slice(0, 6).map((service, index) => {
                  const Icon = services[index % services.length]?.icon || Car;
                  return (
                    <Link key={service.id} href={`/services/${service.id}`}>
                      <Card
                        className="p-6 rounded-2xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden cursor-pointer"
                        data-testid={`service-card-${index}`}
                      >
                        <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                          <Icon className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="text-xl font-semibold mb-2 text-card-foreground">{service.name}</h3>
                        <p className="text-muted-foreground mb-4">
                          {service.description || "Profesyonel araç yıkama hizmeti"}
                        </p>
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="rounded-full">
                            <Clock className="w-3 h-3 mr-1" />
                            {service.durationMinutes} dk
                          </Badge>
                          <span className="text-sm text-primary font-semibold">Detaylar →</span>
                        </div>
                      </Card>
                    </Link>
                  );
                })}
              </div>
              
              <div className="mt-12 text-center">
                <Link href="/services">
                  <Button size="lg" variant="outline" className="px-8 py-4" data-testid="button-all-services">
                    Tüm Hizmetleri Gör
                  </Button>
                </Link>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <Link key={service.id} href={`/services/${service.id}`}>
                    <Card
                      className="p-6 rounded-2xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden cursor-pointer"
                      data-testid={`service-card-${index}`}
                    >
                      <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                      <h3 className="text-xl font-semibold mb-2 text-card-foreground">{service.title}</h3>
                      <p className="text-muted-foreground mb-4">{service.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge variant="secondary" className="rounded-full">
                          <Clock className="w-3 h-3 mr-1" />
                          {service.duration} dk
                        </Badge>
                        <span className="text-sm text-primary font-semibold">Detaylar →</span>
                      </div>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-primary">Fiyatlarımız</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Şeffaf ve uygun fiyatlarla kaliteli hizmet
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pricing.map((plan, index) => (
              <Card
                key={index}
                className="p-8 rounded-3xl hover:shadow-xl transition-all"
                data-testid={`pricing-card-${index}`}
              >
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-semibold mb-2 text-card-foreground">{plan.type}</h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-baseline justify-between border-b border-border pb-3">
                    <span className="text-sm text-muted-foreground">Dış Yıkama</span>
                    <span className="text-2xl font-mono font-bold text-primary">{formatCurrency(plan.base)}</span>
                  </div>
                  <div className="flex items-baseline justify-between border-b border-border pb-3">
                    <span className="text-sm text-muted-foreground">İç + Dış</span>
                    <span className="text-2xl font-mono font-bold text-primary">{formatCurrency(plan.premium)}</span>
                  </div>
                  <div className="flex items-baseline justify-between">
                    <span className="text-sm text-muted-foreground">Komple Bakım</span>
                    <span className="text-2xl font-mono font-bold text-primary">{formatCurrency(plan.full)}</span>
                  </div>
                </div>

                <Link href="/book">
                  <Button className="w-full mt-6 rounded-xl" variant="outline" data-testid={`button-pricing-${index}`}>
                    Randevu Al
                  </Button>
                </Link>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Subscription Packages Section */}
      {subscriptionPackages.length > 0 && (
        <section id="subscriptions" className="py-20 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <Badge className="mb-4 px-4 py-1.5 bg-primary/10 text-primary border-primary/20" data-testid="badge-subscription">
                <Star className="w-4 h-4 mr-1.5 fill-primary" />
                {brandSettings?.subscriptions?.badge || "Abonelik Avantajları"}
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
                {brandSettings?.subscriptions?.title || "Abonelik Paketleri"}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {brandSettings?.subscriptions?.subtitle || "Düzenli bakım ile aracınızı koruyun, zaman kazanın ve özel indirimlerden faydalanın"}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {subscriptionPackages
                .filter(pkg => pkg.isFeatured || pkg.isPopular)
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .slice(0, 3)
                .map((pkg, index) => {
                  const getBillingLabel = (type: string) => {
                    switch (type) {
                      case "monthly": return "Aylık";
                      case "quarterly": return "3 Aylık";
                      case "six_monthly": return "6 Aylık";
                      case "annual": return "Yıllık";
                      default: return type;
                    }
                  };
                  
                  return (
                    <Card 
                      key={pkg.id} 
                      className={`relative p-6 rounded-2xl transition-all duration-300 hover:shadow-xl hover:-translate-y-1 ${
                        pkg.isPopular ? 'border-2 border-primary shadow-lg' : ''
                      }`}
                      data-testid={`subscription-card-${index}`}
                    >
                      {pkg.isPopular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-primary text-primary-foreground px-4 py-1">
                            <Star className="w-3 h-3 mr-1 fill-current" />
                            Popüler
                          </Badge>
                        </div>
                      )}
                      
                      <div className="text-center mb-6 pt-2">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
                          <Package className="w-8 h-8 text-primary" />
                        </div>
                        <h3 className="text-xl font-bold mb-1">{pkg.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          {getBillingLabel(pkg.type)}
                        </Badge>
                      </div>
                      
                      <div className="text-center mb-6">
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-4xl font-bold text-primary">{formatCurrency(pkg.price)}</span>
                          <span className="text-muted-foreground">/{getBillingLabel(pkg.type).toLowerCase()}</span>
                        </div>
                        {pkg.discountPercentage && pkg.discountPercentage > 0 && (
                          <Badge variant="outline" className="mt-2 text-green-600 border-green-600/30 bg-green-50 dark:bg-green-900/20">
                            %{pkg.discountPercentage} İndirim
                          </Badge>
                        )}
                      </div>
                      
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{pkg.exteriorWashCount + pkg.interiorCleanCount} yıkama hakkı</span>
                        </div>
                        {pkg.interiorCleanCount > 0 && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span>{pkg.interiorCleanCount} iç temizlik dahil</span>
                          </div>
                        )}
                        {pkg.priorityBooking && (
                          <div className="flex items-center gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            <span>Öncelikli randevu</span>
                          </div>
                        )}
                        {pkg.description && (
                          <p className="text-sm text-muted-foreground pt-2 border-t">
                            {pkg.description}
                          </p>
                        )}
                      </div>
                      
                      <Link href="/subscriptions">
                        <Button 
                          className="w-full rounded-xl" 
                          variant={pkg.isPopular ? "default" : "outline"}
                          data-testid={`button-subscription-${index}`}
                        >
                          Paketi İncele
                        </Button>
                      </Link>
                    </Card>
                  );
                })}
            </div>
            
            <div className="mt-12 text-center">
              <Link href="/subscriptions">
                <Button size="lg" variant="outline" className="px-8 py-4 rounded-xl" data-testid="button-all-subscriptions">
                  {brandSettings?.subscriptions?.buttonText || "Tüm Abonelik Paketleri"}
                  <Package className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-semibold mb-6 text-primary">
                Neden Bizi Seçmelisiniz?
              </h2>
              <div className="space-y-6">
                {features && features.length > 0 ? (
                  features
                    .sort((a, b) => a.order - b.order)
                    .map((feature, index) => {
                      const iconMap: { [key: string]: any } = {
                        'check': CheckCircle2,
                        'shield': Shield,
                        'clock': Clock,
                        'star': Star,
                        'sparkles': Sparkles,
                        'award': Award,
                        'zap': Zap,
                        'heart': Heart,
                      };
                      const IconComponent = iconMap[feature.icon] || CheckCircle2;
                      return (
                        <div key={feature.id} className="flex gap-4">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-6 h-6 text-primary" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg mb-1 text-primary">{feature.title}</h3>
                            <p className="text-muted-foreground">{feature.description}</p>
                          </div>
                        </div>
                      );
                    })
                ) : (
                  <>
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1 text-primary">Profesyonel Ekip</h3>
                        <p className="text-muted-foreground">Deneyimli ve eğitimli uzman ekibimizle hizmetinizdeyiz</p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1 text-primary">Güvenli Hizmet</h3>
                        <p className="text-muted-foreground">Aracınız bizimle güvende</p>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="relative">
              <div className="rounded-3xl bg-gradient-to-br from-primary/20 to-accent/20 p-8 h-96 flex items-center justify-center">
                <Car className="w-64 h-64 text-primary/20" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-primary">
              Sıkça Sorulan Sorular
            </h2>
            <p className="text-lg text-muted-foreground">
              Merak ettiklerinizin cevapları burada
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full">
            {faqs && faqs.length > 0 ? (
              faqs
                .sort((a, b) => a.order - b.order)
                .map((faq, index) => (
                  <AccordionItem key={faq.id} value={`item-${index}`}>
                    <AccordionTrigger className="text-left" data-testid={`faq-trigger-${index}`}>
                      <span className="text-lg font-medium text-card-foreground">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground" data-testid={`faq-content-${index}`}>
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))
            ) : (
              <>
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-left" data-testid="faq-trigger-1">
                    <span className="text-lg font-medium text-card-foreground">
                      Randevu almak için üye olmam gerekir mi?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground" data-testid="faq-content-1">
                    Hayır, randevu almak için üye olmanıza gerek yok.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-left" data-testid="faq-trigger-2">
                    <span className="text-lg font-medium text-card-foreground">
                      Randevumu nasıl takip edebilirim?
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground" data-testid="faq-content-2">
                    Randevu takip sayfasından rezervasyon kodunuz ile sorgulayabilirsiniz.
                  </AccordionContent>
                </AccordionItem>
              </>
            )}
          </Accordion>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-primary">
              Müşterilerimiz Ne Diyor?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Hizmetlerimizden memnun kalan müşterilerimizin görüşleri
            </p>
          </div>

          {testimonials && testimonials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials
                .sort((a, b) => {
                  if (a.featured && !b.featured) return -1;
                  if (!a.featured && b.featured) return 1;
                  return a.order - b.order;
                })
                .slice(0, 6)
                .map((testimonial, index) => (
                  <Card
                    key={testimonial.id}
                    className="p-6 rounded-2xl hover:shadow-xl transition-all"
                    data-testid={`testimonial-card-${index}`}
                  >
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < testimonial.rating
                              ? "fill-yellow-400 text-yellow-400"
                              : "text-muted-foreground"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-muted-foreground mb-6 italic">
                      "{testimonial.comment}"
                    </p>
                    <div className="border-t border-border pt-4">
                      <p className="font-semibold text-card-foreground">
                        {testimonial.customerName}
                      </p>
                      {(testimonial.position || testimonial.company) && (
                        <p className="text-sm text-muted-foreground">
                          {testimonial.position}
                          {testimonial.position && testimonial.company && ", "}
                          {testimonial.company}
                        </p>
                      )}
                    </div>
                  </Card>
                ))}
            </div>
          ) : (
            <div className="text-center p-8 text-muted-foreground">
              <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-20" />
              <p>Henüz müşteri yorumu bulunmuyor</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact WhatsApp Section */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-primary">
              İletişime Geçin
            </h2>
            <p className="text-lg text-muted-foreground">
              Sorularınız için bizimle iletişime geçebilirsiniz
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {contactSettings?.phoneNumbers && contactSettings.phoneNumbers.length > 0 && (
              <Card className="p-6 text-center hover:shadow-lg transition-all">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2 text-card-foreground">Telefon</h3>
                <a href={`tel:${contactSettings.phoneNumbers[0].replace(/\s/g, '')}`} className="text-muted-foreground hover:text-primary transition-colors" data-testid="link-phone">
                  {contactSettings.phoneNumbers[0]}
                </a>
              </Card>
            )}

            {contactSettings?.emails && contactSettings.emails.length > 0 && (
              <Card className="p-6 text-center hover:shadow-lg transition-all">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2 text-card-foreground">E-posta</h3>
                <a href={`mailto:${contactSettings.emails[0]}`} className="text-muted-foreground hover:text-primary transition-colors" data-testid="link-email">
                  {contactSettings.emails[0]}
                </a>
              </Card>
            )}

            {contactSettings?.address && (
              <Card className="p-6 text-center hover:shadow-lg transition-all">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-2 text-card-foreground">Adres</h3>
                <p className="text-muted-foreground text-sm">
                  {contactSettings.address}
                </p>
              </Card>
            )}
          </div>

          {contactSettings?.whatsappNumber && (
            <div className="text-center">
              <a
                href={`https://wa.me/${contactSettings.whatsappNumber}?text=${encodeURIComponent(contactSettings.whatsappMessage || 'Merhaba, bilgi almak istiyorum')}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="button-whatsapp"
              >
                <Button size="lg" className="px-8 py-6 text-lg rounded-xl bg-[#25D366] hover:bg-[#20BA5A] text-white border-0">
                  <SiWhatsapp className="w-6 h-6 mr-2" />
                  WhatsApp ile İletişime Geç
                </Button>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-primary to-primary/80 rounded-3xl p-12 text-primary-foreground">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Hemen Randevu Alın
            </h2>
            <p className="text-lg mb-8 opacity-90">
              Aracınıza hak ettiği bakımı vermek için bugün randevu oluşturun
            </p>
            <Link href="/book">
              <Button size="lg" variant="secondary" className="px-8 py-6 text-lg rounded-xl" data-testid="button-cta">
                Randevu Sistemi
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Droplets className="w-8 h-8 text-primary" />
                <span className="text-xl font-bold text-primary">TemizFilo</span>
              </div>
              <p className="text-sm text-muted-foreground">
                Profesyonel araç yıkama ve bakım hizmetleri
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Hizmetler</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Dış Yıkama</li>
                <li>İç Temizlik</li>
                <li>Detaylı Bakım</li>
                <li>Motor Temizliği</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Kurumsal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li>Hakkımızda</li>
                <li>İletişim</li>
                <li>
                  <Link href="/privacy">
                    <span className="hover:text-foreground transition-colors cursor-pointer" data-testid="link-privacy">
                      KVKK & Gizlilik
                    </span>
                  </Link>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">İletişim</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {contactSettings?.phoneNumbers && contactSettings.phoneNumbers.length > 0 && (
                  <li>Tel: {contactSettings.phoneNumbers[0]}</li>
                )}
                {contactSettings?.emails && contactSettings.emails.length > 0 && (
                  <li>Email: {contactSettings.emails[0]}</li>
                )}
                {contactSettings?.address && (
                  <li>Adres: {contactSettings.address}</li>
                )}
              </ul>
            </div>
          </div>
          
          <div className="mt-12 pt-8 border-t border-border text-center text-sm text-muted-foreground">
            <p>&copy; {new Date().getFullYear()} TemizFilo. Tüm hakları saklıdır.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <WhatsAppButton />
    </div>
  );
}
